import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import axios from "axios";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import SuccessModal from "./SuccessModal";
import FailedModal from "./FailedModal";
import countriesData from "../assets/countries.json";
import sectorsData from "../assets/sectors.json";

const RegistrationForm = ({ isVisitor, isSpeaker, isExhibitor }) => {
  const { t, i18n } = useTranslation();
  const [successModal, setSuccessModal] = useState(false);
  const [failedModal, setFailedModal] = useState(false);
  const [policyModal, setPolicyModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [selectedSector, setSelectedSector] = useState("");
  const [uuid, setUUID] = useState("");

  const [isChecked, setIsChecked] = useState(false);

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const tog_modal = () => {
    setSuccessModal(!successModal);
    return successModal;
  };

  const tog_failed_modal = () => {
    setFailedModal(!failedModal);
    return failedModal;
  };

  const tog_policy_modal = () => {
    setPolicyModal(!policyModal);
    return policyModal;
  };

  const initialValues = {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    job_title: "",
    company: "",
    message: "",
    ...(!isExhibitor ? {} : { website_url: "" }),
    ...(!isExhibitor ? {} : { country: "" }),
    ...(!isExhibitor ? {} : { city: "" }),
    ...(!isExhibitor ? {} : { sex: "" }),
    ...(!isExhibitor ? {} : { sector: "" }),
  };

  const countryOptions = countriesData.map((country) => ({
    value: country.name,
    label: i18n.language === "en" ? country.en : country.ar,
  }));

  const genderOptions = [
    {
      value: "male",
      label: i18n.language === "en" ? "male" : "ذكر",
    },
    {
      value: "female",
      label: i18n.language === "en" ? "female" : "أنثى",
    },
  ];

  const sectorOptions = sectorsData.map((sector) => ({
    value: sector.value,
    label: i18n.language === "en" ? sector.en : sector.ar,
  }));

  const handleCountryChange = (selectedOption) => {
    setSelectedCountry(selectedOption);
  };

  const handleGenderChange = (selectedOption) => {
    setSelectedGender(selectedOption);
  };

  const handleSectorChange = (selectedOption) => {
    setSelectedSector(selectedOption);
  };

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required(t("registrationForm.firstNameRequired")),
    last_name: Yup.string().required(t("registrationForm.lastNameRequired")),
    email: Yup.string()
      .email(t("registrationForm.invalidEmail"))
      .required(t("registrationForm.emailRequired")),
    phone: Yup.string().required(t("registrationForm.phoneRequired")),
    job_title: Yup.string().required(t("registrationForm.jobTitleRequired")),
    company: Yup.string().required(t("registrationForm.companyRequired")),
    message: Yup.string(),
  });

  const handleSubmit = async (values, { resetForm }) => {
    const url = isVisitor
      ? "https://admin.fmexcon.com/api/userRegisterStore"
      : isExhibitor
      ? "https://admin.fmexcon.com/api/sponsorRegisterStore"
      : isSpeaker
      ? "https://admin.fmexcon.com/api/conferanceRegisterStore"
      : "";

    await axios
      .post(url, {
        ...values,
        phone: `+${values?.phone}`,
        country: selectedCountry?.label,
        gender: selectedGender?.label,
        sector: selectedSector?.label,
      })
      .then((res) => {
        if (res.status === 200) {
          const responseUuid = res?.data?.data?.uuid || res?.data?.uuid;
          if (responseUuid) {
            setUUID(responseUuid);
          }
          setSuccessModal(true);
          resetForm();
        } else {
          setFailedModal(true);
          resetForm();
        }
      })
      .catch((data) => {
        toast.error(
          data?.response?.data?.errors?.phone?.[0]
            ? data?.response?.data?.errors?.phone[0]
            : t("common.submit_error")
        );
        throw new Error("Unexpected response");
      });
  };

  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "white",
      borderColor: "#e5e7eb",
      borderRadius: "8px",
      padding: "1.5px",
      boxShadow: "none",
      fontWeight: "600",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#013047a8",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#013047" : "white",
      color: state.isSelected ? "white" : "#013047",
      "&:hover": {
        backgroundColor: "#013047",
        color: "white",
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 10,
    }),
  };

  return (
    <div
      className={`col-span-12 mb-6 ${
        i18n.language === "en" ? "text-left" : "text-right"
      }`}
      dir={i18n.language === "en" ? "ltr" : "rtl"}
    >
      <SuccessModal
        tog_modal={tog_modal}
        successModal={successModal}
        uuid={uuid}
      />
      <FailedModal
        tog_failed_modal={tog_failed_modal}
        failedModal={failedModal}
      />
      <div
        data-aos="fade-left"
        data-aos-duration="500"
        className=" transition-opacity"
      >
        {policyModal && (
          <div
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
            onClick={() => {
              tog_policy_modal();
            }}
          >
            <div
              className="bg-white rounded-lg shadow-lg p-6 md:w-[500px] w-[85%] text-center relative text-text"
              onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the modal
            >
              <p
                className={`text-lg cursor-pointer -mt-4 ${
                  i18n.language === "en" ? "text-left" : "text-right"
                }`}
                onClick={() => {
                  tog_policy_modal();
                }}
              >
                x
              </p>
              <p className="text-base font-bold mb-2">
                {t("registrationForm.privacy_policy")}
              </p>
              <p className="text-[10px] text-justify">
                {" "}
                {t("registrationForm.privacy_policy_content")}
              </p>
            </div>
          </div>
        )}
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, values, setFieldValues }) => (
          <Form className="w-full mx-auto p-4 relative">
            {isExhibitor ? (
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                  <div className="flex flex-col">
                    <Field
                      name="first_name"
                      placeholder={t("registrationForm.firstNamePlaceholder")}
                      className="w-full bg-white rounded-lg border border-gray-200 outline-none p-2 placeholder:text-[#013047a8] font-semibold"
                    />
                    <ErrorMessage
                      name="first_name"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>

                  <div className="flex flex-col">
                    <Field
                      name="last_name"
                      placeholder={t("registrationForm.lastNamePlaceholder")}
                      className="w-full bg-white rounded-lg border border-gray-200 outline-none p-2 placeholder:text-[#013047a8] font-semibold"
                    />
                    <ErrorMessage
                      name="last_name"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                </div>
                <div className="flex flex-col mb-4">
                  <Field
                    name="email"
                    type="email"
                    placeholder={t("registrationForm.emailPlaceholder")}
                    className="w-full bg-white rounded-lg border border-gray-200 outline-none p-2 placeholder:text-[#013047a8] font-semibold"
                  />
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="text-red-500 text-sm"
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                  <div className="flex flex-col" dir="rtl">
                    <PhoneInput
                      country="sa"
                      value={values.phone}
                      onChange={(phone) => setFieldValue("phone", phone)}
                      inputProps={{
                        name: "phone",
                        id: "phone",
                        className:
                          "border-[1.5px] px-2 py-[9px]  w-full bg-white  rounded-lg border border-gray-200 phone-input-handle",
                        placeholder: t("registrationForm.phone"),
                      }}
                      dropdownClass="custom-phone-dropdown"
                    />
                    <ErrorMessage
                      name="phone"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                  <div className="flex flex-col">
                    <Field
                      name="website_url"
                      placeholder={t("registrationForm.websiteUrlPlaceholder")}
                      className="w-full bg-white rounded-lg border border-gray-200 outline-none p-2 placeholder:text-[#013047a8] font-semibold"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                  <div className="flex flex-col">
                    <Field
                      name="job_title"
                      placeholder={t("registrationForm.jobTitlePlaceholder")}
                      className="w-full bg-white rounded-lg border border-gray-200 outline-none p-2 placeholder:text-[#013047a8] font-semibold"
                    />
                    <ErrorMessage
                      name="job_title"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                  <div className="flex flex-col">
                    <Field
                      name="company"
                      placeholder={t("registrationForm.companyPlaceholder")}
                      className="w-full bg-white rounded-lg border border-gray-200 outline-none p-2 placeholder:text-[#013047a8] font-semibold"
                    />
                    <ErrorMessage
                      name="company"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                </div>

                <div className="flex flex-col mb-4">
                  <Field
                    as="textarea"
                    name="message"
                    placeholder={t("registrationForm.messagePlaceholder")}
                    rows="4"
                    className="w-full bg-white rounded-lg border border-gray-200 outline-none p-2 placeholder:text-[#013047a8] font-semibold"
                  />
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    className="px-6 py-2 bg-text hover:bg-text_hover text-white rounded-lg"
                  >
                    {t("registrationForm.submit")}
                  </button>
                </div>
              </div>
            ) : null}
            {isVisitor || isSpeaker ? (
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                  <div className="flex flex-col">
                    <Field
                      name="first_name"
                      placeholder={t("registrationForm.firstNamePlaceholder")}
                      className="w-full bg-white rounded-lg border border-gray-200 outline-none p-2 placeholder:text-[#013047a8] font-semibold"
                    />
                    <ErrorMessage
                      name="first_name"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>

                  <div className="flex flex-col">
                    <Field
                      name="last_name"
                      placeholder={t("registrationForm.lastNamePlaceholder")}
                      className="w-full bg-white rounded-lg border border-gray-200 outline-none p-2 placeholder:text-[#013047a8] font-semibold"
                    />
                    <ErrorMessage
                      name="last_name"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                  <div className="flex flex-col mb-4">
                    <Field
                      name="email"
                      type="email"
                      placeholder={t("registrationForm.emailPlaceholder")}
                      className="w-full bg-white rounded-lg border border-gray-200 outline-none p-2 placeholder:text-[#013047a8] font-semibold"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                  <div className="flex flex-col" dir="rtl">
                    <PhoneInput
                      country="sa"
                      value={values.phone}
                      onChange={(phone) => setFieldValue("phone", phone)}
                      inputProps={{
                        name: "phone",
                        id: "phone",
                        className:
                          "border-[1.5px] px-2 py-[9px]  w-full bg-white  rounded-lg border border-gray-200 phone-input-handle",
                        placeholder: t("registrationForm.phone"),
                      }}
                      dropdownClass="custom-phone-dropdown"
                    />
                    <ErrorMessage
                      name="phone"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                  <div className="flex flex-col">
                    <Field
                      name="job_title"
                      placeholder={t("registrationForm.jobTitlePlaceholder")}
                      className="w-full bg-white rounded-lg border border-gray-200 outline-none p-2 placeholder:text-[#013047a8] font-semibold"
                    />
                    <ErrorMessage
                      name="job_title"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                  <div className="flex flex-col">
                    <Field
                      name="company"
                      placeholder={t("registrationForm.companyPlaceholder")}
                      className="w-full bg-white rounded-lg border border-gray-200 outline-none p-2 placeholder:text-[#013047a8] font-semibold"
                    />
                    <ErrorMessage
                      name="company"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                  <div className="flex flex-col">
                    <Select
                      options={genderOptions}
                      onChange={handleGenderChange}
                      placeholder={t("registrationForm.gender")}
                      isSearchable
                      styles={customStyles}
                      className="w-full mx-auto py-[1px]"
                    />
                    <ErrorMessage
                      name="country"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                  <div className="flex flex-col">
                    <Select
                      options={sectorOptions}
                      onChange={handleSectorChange}
                      placeholder={t("registrationForm.sector")}
                      isSearchable
                      styles={customStyles}
                      className="w-full mx-auto py-[1px]"
                    />
                    <ErrorMessage
                      name="country"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                  <div className="flex flex-col">
                    <Select
                      options={countryOptions}
                      onChange={handleCountryChange}
                      placeholder={t("registrationForm.country")}
                      isSearchable
                      styles={customStyles}
                      className="w-full mx-auto py-[1px]"
                    />
                    <ErrorMessage
                      name="country"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                  <div className="flex flex-col">
                    <Field
                      name="city"
                      placeholder={t("registrationForm.city")}
                      className="w-full bg-white rounded-lg border border-gray-200 outline-none p-2 placeholder:text-[#013047a8] font-semibold"
                    />
                    <ErrorMessage
                      name="city"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                </div>

                <div className="flex flex-col mb-4">
                  <Field
                    as="textarea"
                    name="message"
                    placeholder={t("registrationForm.messagePlaceholder")}
                    rows="4"
                    className="w-full bg-white rounded-lg border border-gray-200 outline-none p-2 placeholder:text-[#013047a8] font-semibold"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                    className="tracking-wide cursor-pointer"
                    required
                  />
                  <span className="mx-[6px] tracking-normal">
                    {t("registrationForm.accept")}
                  </span>{" "}
                  <div
                    onClick={tog_policy_modal}
                    className="text-text underline tracking-normal cursor-pointer"
                  >
                    {t("registrationForm.terms")}
                  </div>
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    className="px-6 py-2 bg-text hover:bg-text_hover text-white rounded-lg"
                  >
                    {t("registrationForm.submit")}
                  </button>
                </div>
              </div>
            ) : null}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default RegistrationForm;
